import {
    Component,
    ElementRef,
    forwardRef,
    Input,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ToolbarOptions, ToolbarSettings } from './text-editor-toolbar.types';
import Quill, { Delta } from 'quill';
import { TextEditorToolbarUtils } from './text-editor.utils';
import { FileHelper } from 'src/app/helpers/files/file.helper';
import { EventEmitter } from '@angular/core';

@Component({
    selector: 'app-text-editor',
    templateUrl: './text-editor.component.html',
    styleUrls: ['./text-editor.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TextEditorComponent),
            multi: true,
        },
    ],
})
export class TextEditorComponent implements OnInit {
    @Input() content: string = 'your content here';
    @Input() placeholder: string;
    // Use the defined type for toolbar options
    @Input() toolbarOptions: ToolbarOptions| false = TextEditorToolbarUtils.BASIC_TOOLBAR;
    @Input() toolbarSettings: ToolbarSettings = { disableToolbar: false, disableImageDrop: false, disableTextUpdateOnChange: false };

    @Input() height: string;

    @Output() blur: EventEmitter<any> = new EventEmitter();

    @ViewChild('editor') editorElement: ElementRef;

    editorHeight: string = '200px';
    editor: any;
    value: string = '';
    disabled: boolean = false;
    onChange: any = () => {};
    onTouched: any = () => {};

    constructor(private fileHelper: FileHelper) {}

    ngOnInit(): void {
        if(this.toolbarSettings.disableToolbar || this.toolbarOptions == false){
            this.toolbarOptions = (this.toolbarOptions == false)? false : TextEditorToolbarUtils.EMPTY_TOOLBAR;
            return;
        }
    }

    ngAfterViewInit() {
        this.initialize();
    }
    ngOnChanges(changes: SimpleChanges) {
        if (changes.height) {
            this.editorHeight = changes.height.currentValue || '100%';
        }
    }

    initialize() {

        this.editor = new Quill(this.editorElement.nativeElement, {
            modules: {
                toolbar: {
                    container: (this.toolbarOptions) ? this.toolbarOptions : [],
                    handlers: {
                        image: function () {
                            const input = document.createElement('input');
                            input.setAttribute('type', 'file');
                            input.setAttribute('accept', 'image/*');
                            input.click(); // Open file explorer

                            input.onchange = () => {
                                const file = input.files?.[0];

                                if (!file) return;

                                if (
                                    !this.fileHelper.isValidImageSize(file.size)
                                )
                                    return;

                                // Convert image to base64 and insert
                                const reader = new FileReader();
                                reader.onload = () => {
                                    const range = this.editor.getSelection();
                                    this.editor.insertEmbed(
                                        range.index,
                                        'image',
                                        reader.result as string
                                    );
                                };
                                reader.readAsDataURL(file);
                            };
                        }.bind(this),
                    },
                },
            },
            placeholder: this.placeholder,
            theme: 'snow',
        });  

        if(this.toolbarSettings.disableImageDrop) {
            this.editor.root.addEventListener('dragover', (event) => {
                event.preventDefault();
                event.stopPropagation();
                event.dataTransfer.dropEffect = 'none';
                return false;
              }, false);
        }

        if(this.toolbarSettings.disableImagePaste) {
            this.editor.root.addEventListener('paste', (event: ClipboardEvent) => { 
                const hasImageFiles = Array.from(event.clipboardData.items).some(
                    item => item.type.startsWith('image')
                  );

                if(hasImageFiles) return true;

                event.preventDefault();
                event.stopPropagation();            
                return false;
            }, true);
        }

        // Prevent large images from being dropped
        this.editor.root.addEventListener('drop', (event: DragEvent) => {
            const file = event.dataTransfer?.files?.[0];
            if (this.fileHelper.isValidImageSize(file.size)) return true;
            event.preventDefault(); // Stop image insertion
            event.stopPropagation();
            return false;
        },true);

        this.editor.on('text-change', () => {
            if(this.toolbarSettings.disableTextUpdateOnChange) return;
            this.emitTextChange();
        });

        this.editor.on('selection-change', (range: any) => {
            if (!range) {
                if(this.toolbarSettings.disableTextUpdateOnChange){
                   this.emitTextChange();
                }
                this.blur.emit(range);
            }
            this.onTouched();
        });

        if (this.value) {
            this.editor.clipboard.dangerouslyPasteHTML(this.value);
        }
    }
    emitTextChange(){
        const html =
                this.editorElement.nativeElement.querySelector(
                    '.ql-editor'
                ).innerHTML;
        this.onChange(html);
    }
    writeValue(value: string): void {
        this.value = value || '';

        if (this.editor) {
            if (value) {
                this.editor.clipboard.dangerouslyPasteHTML(value);
            } else {
                this.editor.setText('');
            }
        }
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (this.editor) {
            this.editor.enable(!isDisabled);
        }
    }
}
